import { useCallback, useEffect, useRef, useState } from 'react'
import {
  REGISTRY_KIND_LABELS, importHubApi, type HubImportResult, type HubImportType, type ProductImportField, type ProductImportResult,
  type RegistryKind, type RegistryRow,
} from '../api/importHub'
import { importMappingsApi, type ImportMapping } from '../api/importMappings'
import { CSV_ACCEPT, readTabularFile } from '../lib/spreadsheet'
import { dateIt, errMsg } from '../lib/financeFormat'
import {
  Button, Callout, Card, CardContent, Checkbox, DataTable, DataTableEmptyRow, LoadingState, Select, StatusPill, TextField,
  useConfirm, useSnackbar, type PillTone,
} from './ui'
import { useFeatureFlag } from '../lib/featureFlags'

/**
 * C-17 (REQ-238-001) - pannelli della pagina Importazioni dietro il flag `import.hub`: "Registro" (tutti i lotti di ogni
 * modulo, sola lettura) e "Prodotti" (unico import nuovo: mappatura salvata, anteprima senza scrittura, applicazione
 * idempotente che crea solo i prodotti mancanti).
 */

const STATUS_TONE: Record<string, PillTone> = { Applicato: 'success', applied: 'success', Annullato: 'warning', Anteprima: 'info' }
const KINDS = Object.keys(REGISTRY_KIND_LABELS) as RegistryKind[]

export function ImportRegistryPanel() {
  const [kind, setKind] = useState<RegistryKind | ''>('')
  const [rows, setRows] = useState<RegistryRow[] | null>(null)
  const [error, setError] = useState('')
  const confirm = useConfirm()
  const snackbar = useSnackbar()

  const load = useCallback(async () => {
    setError('')
    try {
      setRows((await importHubApi.registry(kind || undefined)).items)
    } catch (e) {
      setError(errMsg(e, 'Impossibile caricare il registro.'))
    }
  }, [kind])
  useEffect(() => { void load() }, [load])

  async function undo(r: RegistryRow) {
    if (!r.undoUrl) return
    if (!(await confirm({
      title: 'Annullare il lotto?',
      message: `Annullare il lotto di ${REGISTRY_KIND_LABELS[r.kind]} (${r.source ?? 'senza nome'})? Cio' che e' stato modificato dopo l'importazione non viene toccato.`,
      confirmLabel: 'Annulla lotto', destructive: true,
    }))) return
    try {
      const res = await importHubApi.undo(r.undoUrl)
      const done = (res.undone ?? 0) + (res.deactivated ?? 0) + (res.restored ?? 0)
      snackbar.show({ message: `Lotto annullato: ${done} elementi${res.skipped > 0 ? `, ${res.skipped} lasciati com'erano` : ''}.`, tone: 'success' })
      await load()
    } catch (e) {
      setError(errMsg(e, 'Annullo non riuscito.'))
    }
  }

  return (
    <>
      {error && <Callout tone="error">{error}</Callout>}
      <Callout tone="info">
        Registro di tutti i lotti. Lead e clienti si annullano dal tab “Storico lotti”; prodotti, spese, crediti attesi e provvigioni
        storiche da qui (quando l'hub esteso e' attivo); contratti storici e pagamenti NUMBERS da riga di comando; gli altri tipi non si annullano.
      </Callout>
      <Card variant="outlined">
        <CardContent>
          <Select label="Tipo" value={kind} onChange={e => setKind(e.target.value as RegistryKind | '')}>
            <option value="">Tutti</option>
            {KINDS.map(k => <option key={k} value={k}>{REGISTRY_KIND_LABELS[k]}</option>)}
          </Select>
        </CardContent>
      </Card>
      {rows === null ? <LoadingState /> : (
        <DataTable aria-label="Registro dei lotti">
          <thead>
            <tr>
              <th>Quando</th><th>Tipo</th><th>Origine</th><th>Chi</th>
              <th className="text-end">Righe</th><th className="text-end">Ok</th><th className="text-end">Problemi</th>
              <th>Stato</th><th>Annulla</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <DataTableEmptyRow colSpan={9} title="Nessun lotto" description="" />}
            {rows.map(r => (
              <tr key={`${r.kind}-${r.id}`}>
                <td>{dateIt(r.createdAt)}</td>
                <td>{REGISTRY_KIND_LABELS[r.kind]}</td>
                <td>{r.source ?? '—'}</td>
                <td>{r.createdByName ?? '—'}</td>
                <td className="text-end md-num">{r.totalRows}</td>
                <td className="text-end md-num">{r.okRows}</td>
                <td className="text-end md-num">{r.problemRows}</td>
                <td><StatusPill tone={STATUS_TONE[r.status] ?? 'neutral'}>{r.status}</StatusPill></td>
                <td>
                  {r.undoUrl?.startsWith('/api/imports/') ? 'da “Storico lotti”'
                    : r.undoUrl ? <Button variant="outlined" size="sm" onClick={() => void undo(r)}>Annulla</Button>
                      : r.kind === 'contracts_history' || r.kind === 'numbers_payments' ? 'da riga di comando' : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
    </>
  )
}

export function ProductImportPanel() {
  const [fields, setFields] = useState<ProductImportField[]>([])
  const [rule, setRule] = useState('')
  const [saved, setSaved] = useState<ImportMapping[]>([])
  const [savedId, setSavedId] = useState('')
  const [csv, setCsv] = useState('')
  const [fileName, setFileName] = useState<string | null>(null)
  const [result, setResult] = useState<ProductImportResult | null>(null)
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [mapName, setMapName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [updateExisting, setUpdateExisting] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const snackbar = useSnackbar()
  const extended = useFeatureFlag('import.hub_esteso')

  const loadBase = useCallback(async () => {
    try {
      const [f, m] = await Promise.all([importHubApi.productFields(), importMappingsApi.list('products')])
      setFields(f.fields)
      setRule(f.rule)
      setSaved(m.items)
    } catch (e) {
      setError(errMsg(e, 'Impossibile caricare i campi.'))
    }
  }, [])
  useEffect(() => { void loadBase() }, [loadBase])

  async function pick(file: File) {
    setError('')
    setResult(null)
    try {
      setCsv(await readTabularFile(file))
      setFileName(file.name)
    } catch (e) {
      setError(errMsg(e, 'File non leggibile.'))
    }
  }

  const chosen = () => saved.find(s => String(s.id) === savedId)?.mapping ?? null
  const currentMapping = () => (Object.keys(mapping).length > 0 ? mapping : chosen())

  async function run(apply: boolean) {
    setBusy(true)
    setError('')
    try {
      const r = apply
        ? await importHubApi.productApply(csv, fileName, currentMapping(), extended && updateExisting)
        : await importHubApi.productPreview(csv, fileName, currentMapping(), extended && updateExisting)
      setResult(r)
      setMapping(r.appliedMapping)
      if (apply) snackbar.show({ message: `Importazione completata: ${r.created} prodotti creati, ${r.updated ?? 0} aggiornati.`, tone: 'success' })
    } catch (e) {
      setError(errMsg(e, 'Importazione non riuscita.'))
    } finally {
      setBusy(false)
    }
  }

  async function saveMapping() {
    const name = mapName.trim()
    if (!name) { setError('Dai un nome alla mappatura da salvare.'); return }
    try {
      const existing = saved.find(s => s.isOwner && s.name === name)
      if (existing) await importMappingsApi.update(existing.id, { kind: 'products', name, mapping, shared: false })
      else await importMappingsApi.create({ kind: 'products', name, mapping, shared: false })
      setMapName('')
      snackbar.show({ message: `Mappatura “${name}” salvata.`, tone: 'success' })
      await loadBase()
    } catch (e) {
      setError(errMsg(e, 'Salvataggio non riuscito.'))
    }
  }

  const label = (key: string) => fields.find(f => f.key === key)?.label ?? key

  return (
    <>
      {error && <Callout tone="error">{error}</Callout>}
      <Card variant="outlined">
        <CardContent>
          <TextField
            ref={fileRef as React.Ref<HTMLInputElement | HTMLTextAreaElement>}
            label="File CSV dei prodotti (massimo 5 MB, separatore , o ;)" type="file" accept={CSV_ACCEPT} disabled={busy}
            onChange={e => { const f = (e.currentTarget as HTMLInputElement).files?.[0]; if (f) void pick(f) }}
          />
          {saved.length > 0 && (
            <Select label="Mappatura salvata (facoltativa)" value={savedId} onChange={e => { setSavedId(e.target.value); setMapping({}) }}>
              <option value="">Riconosci le colonne automaticamente</option>
              {saved.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          )}
          {extended && (
            <Checkbox
              label="Aggiorna i prodotti già presenti con lo stesso codice (l'anteprima mostra le differenze)"
              checked={updateExisting} onChange={e => { setUpdateExisting(e.target.checked); setResult(null) }}
            />
          )}
          {rule && <p className="text-muted md-typescale-body-small mt-3 mb-0">{rule}</p>}
          <Button className="mt-3" variant="outlined" disabled={busy || csv === ''} onClick={() => void run(false)}>
            {busy ? 'Analisi…' : 'Anteprima (non scrive)'}
          </Button>
        </CardContent>
      </Card>
      {result && (
        <Card variant="outlined">
          <CardContent>
            <p className="md-typescale-body-medium">
              {result.dryRun ? 'Anteprima' : 'Applicato'}: {result.total} righe - {result.created} {result.dryRun ? 'da creare' : 'creati'},{' '}
              {result.updated ?? 0} {result.dryRun ? 'da aggiornare' : 'aggiornati'}, {result.existing} già presenti, {result.invalid} non validi.
            </p>
            {Object.keys(result.appliedMapping).length > 0 && (
              <p className="text-muted md-typescale-body-small">
                Colonne: {Object.entries(result.appliedMapping).map(([h, f]) => `${h} → ${label(f)}`).join(' · ')}
              </p>
            )}
            <div className="d-flex gap-2 align-items-end flex-wrap">
              <TextField label="Nome mappatura" value={mapName} onChange={e => setMapName(e.target.value)} />
              <Button variant="outlined" size="sm" onClick={() => void saveMapping()}>Salva mappatura</Button>
              {result.dryRun && (result.created > 0 || (result.updated ?? 0) > 0) && (
                <Button variant="filled" disabled={busy} onClick={() => void run(true)}>Conferma importazione</Button>
              )}
            </div>
            <DataTable aria-label="Righe del file">
              <thead><tr><th>Riga</th><th>Esito</th><th>Nome</th><th>Codice</th><th>Problema / differenze</th></tr></thead>
              <tbody>
                {result.rows.map(r => (
                  <tr key={r.line}>
                    <td>{r.line}</td>
                    <td><StatusPill tone={r.outcome === 'Nuovo' ? 'success' : r.outcome === 'Aggiornato' ? 'info' : r.outcome === 'Non valido' ? 'error' : 'neutral'}>{r.outcome}</StatusPill></td>
                    <td>{r.name ?? '—'}</td><td>{r.code ?? '—'}</td>
                    <td>
                      {r.problem ?? ''}
                      {r.changes?.map(c => <div key={c.field}>{c.field}: {c.from || '—'} → {c.to || '—'}</div>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </CardContent>
        </Card>
      )}
    </>
  )
}

/** R3-24 (REQ-238-001): spese, crediti attesi e provvigioni storiche da CSV (o xlsx). Anteprima senza scrittura, applicazione idempotente, annullo dal registro. */
export function HubTypesImportPanel() {
  const [types, setTypes] = useState<HubImportType[]>([])
  const [kind, setKind] = useState<RegistryKind | ''>('')
  const [csv, setCsv] = useState('')
  const [fileName, setFileName] = useState<string | null>(null)
  const [result, setResult] = useState<HubImportResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const snackbar = useSnackbar()

  useEffect(() => {
    importHubApi.hubTypes().then(t => { setTypes(t); if (t[0]) setKind(t[0].kind) })
      .catch(e => setError(errMsg(e, 'Impossibile caricare i tipi.')))
  }, [])

  const current = types.find(t => t.kind === kind)

  async function pick(file: File) {
    setError('')
    setResult(null)
    try {
      setCsv(await readTabularFile(file))
      setFileName(file.name)
    } catch (e) {
      setError(errMsg(e, 'File non leggibile.'))
    }
  }

  async function run(apply: boolean) {
    if (!kind) return
    setBusy(true)
    setError('')
    try {
      const r = apply ? await importHubApi.hubApply(kind, csv, fileName) : await importHubApi.hubPreview(kind, csv, fileName)
      setResult(r)
      if (apply) snackbar.show({ message: `Importazione completata: ${r.created} righe create.`, tone: 'success' })
    } catch (e) {
      setError(errMsg(e, 'Importazione non riuscita.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {error && <Callout tone="error">{error}</Callout>}
      <Card variant="outlined">
        <CardContent>
          <Select label="Tipo di dati" value={kind} onChange={e => { setKind(e.target.value as RegistryKind); setResult(null) }}>
            {types.map(t => <option key={t.kind} value={t.kind}>{t.label}</option>)}
          </Select>
          {current && (
            <p className="text-muted md-typescale-body-small mt-3">
              Colonne attese (prima riga): {current.columns.join(', ')}. Le righe già presenti non vengono duplicate; il lotto si annulla dal Registro unico.
            </p>
          )}
          <TextField
            label="File CSV o Excel (massimo 5 MB)" type="file" accept=".csv,.txt,.xlsx" disabled={busy}
            onChange={e => { const f = (e.currentTarget as HTMLInputElement).files?.[0]; if (f) void pick(f) }}
          />
          <Button className="mt-3" variant="outlined" disabled={busy || csv === '' || !kind} onClick={() => void run(false)}>
            {busy ? 'Analisi…' : 'Anteprima (non scrive)'}
          </Button>
        </CardContent>
      </Card>
      {result && (
        <Card variant="outlined">
          <CardContent>
            <p className="md-typescale-body-medium">
              {result.dryRun ? 'Anteprima' : 'Applicato'}: {result.total} righe - {result.created} {result.dryRun ? 'da creare' : 'create'},{' '}
              {result.existing} già presenti, {result.invalid} non valide.
            </p>
            {result.dryRun && result.created > 0 && (
              <Button variant="filled" disabled={busy} onClick={() => void run(true)}>Conferma importazione</Button>
            )}
            <DataTable aria-label="Righe del file">
              <thead><tr><th>Riga</th><th>Esito</th><th>Problema</th></tr></thead>
              <tbody>
                {result.rows.map(r => (
                  <tr key={r.line}>
                    <td>{r.line}</td>
                    <td><StatusPill tone={r.outcome === 'Nuovo' ? 'success' : r.outcome === 'Non valido' ? 'error' : 'neutral'}>{r.outcome}</StatusPill></td>
                    <td>{r.problem ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </CardContent>
        </Card>
      )}
    </>
  )
}
